import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, Layers } from "lucide-react";
import HeldFundsPicker from "./HeldFundsPicker";

export default function OverlapTool({ clientId }) {
  const [selected, setSelected] = useState([]);
  const [threshold, setThreshold] = useState(60);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (selected.length < 2) { toast.error("Pick at least 2 funds"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tools/overlap", {
        fund_selections: selected,
        concentration_threshold_pct: Number(threshold),
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Layers className="h-4 w-4 text-primary" />
          <h3 className="font-dashboard text-base font-bold">Fund selection</h3>
        </div>
        <HeldFundsPicker clientId={clientId} value={selected} onChange={setSelected} minPicks={2} />

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs">Concentration threshold (%)</Label>
            <Input type="number" min={10} max={100} value={threshold} onChange={(e) => setThreshold(e.target.value)} className="tabular-nums" data-testid="threshold-input" />
            <p className="mt-1 text-xs text-muted-foreground">Any market-cap band or sector above this share of the aggregate raises a diversification warning.</p>
          </div>
          <div className="flex items-end">
            <Button onClick={run} disabled={busy || selected.length < 2} className="w-full" data-testid="run-overlap-btn">
              {busy ? "Analysing…" : `Analyse ${selected.length} funds`}
            </Button>
          </div>
        </div>
      </Card>

      {result && <OverlapResult result={result} />}
    </div>
  );
}

function OverlapResult({ result }) {
  return (
    <div className="space-y-6" data-testid="overlap-result">
      {result.warnings.length > 0 && (
        <Card className="border-l-4 border-l-destructive border-border bg-white p-5" data-testid="overlap-warnings">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h4 className="font-dashboard font-bold">Low diversification</h4>
          </div>
          <ul className="space-y-1 text-sm">
            {result.warnings.map((w, i) => (
              <li key={i} className="text-destructive">{w.message}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="border-border bg-white p-6">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="font-dashboard font-bold">Aggregate composition</h4>
          <span className="text-xs text-muted-foreground">Weighted by · {result.weight_mode === "amount_invested" ? "amount invested" : "equal"}</span>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <StackedBar title="Market-cap breakdown" data={result.aggregate.market_cap_allocation} palette={["#0071E3", "#5AC8FA", "#8E8E93"]} testId="agg-marketcap" />
          <StackedBar title="Sector breakdown" data={result.aggregate.sector_allocation} palette={["#0071E3", "#34C759", "#FF9500", "#AF52DE", "#FF3B30", "#5856D6", "#5AC8FA", "#FFCC00", "#8E8E93"]} testId="agg-sector" />
        </div>
      </Card>

      <Card className="border-border bg-white p-6">
        <h4 className="mb-4 font-dashboard font-bold">Pairwise overlap</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <th className="pb-2">Fund A</th>
                <th className="pb-2">Fund B</th>
                <th className="pb-2 text-right">Market-cap overlap</th>
                <th className="pb-2 text-right">Sector overlap</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {result.pairwise.map((p, i) => (
                <tr key={i} data-testid={`pair-${i}`}>
                  <td className="py-3 font-medium">{p.fund_a}</td>
                  <td className="py-3 font-medium">{p.fund_b}</td>
                  <td className="py-3 text-right tabular-nums">
                    <OverlapCell value={p.market_cap_overlap_pct} />
                  </td>
                  <td className="py-3 text-right tabular-nums">
                    <OverlapCell value={p.sector_overlap_pct} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function OverlapCell({ value }) {
  const tone = value >= 75 ? "text-destructive" : value >= 50 ? "text-accent" : "text-foreground";
  return <span className={tone + " font-semibold"}>{value.toFixed(1)}%</span>;
}

function StackedBar({ title, data, palette, testId }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0.05);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return (
    <div data-testid={testId}>
      <h5 className="mb-2 text-xs font-semibold text-muted-foreground">{title}</h5>
      <div className="mb-3 flex h-6 overflow-hidden rounded-md">
        {entries.map(([k, v], i) => (
          <div
            key={k}
            title={`${labelFor(k)} · ${v.toFixed(1)}%`}
            style={{ width: `${(v / total) * 100}%`, background: palette[i % palette.length] }}
          />
        ))}
      </div>
      <ul className="space-y-1 text-xs">
        {entries.map(([k, v], i) => (
          <li key={k} className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: palette[i % palette.length] }} />
              {labelFor(k)}
            </span>
            <span className="tabular-nums font-semibold">{v.toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function labelFor(k) {
  return k.replace("_pct", "").replaceAll("_", " ").replace(/^\w/, (c) => c.toUpperCase());
}
